import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DsButton from '../../common/DsButton';
import QuickTaskDetailModal from '../../common/quickTask/QuickTaskDetailModal';
import QuickTaskItem from '../../common/quickTask/QuickTaskItem';
import QuickTaskSkeletonList from '../../common/quickTask/QuickTaskSkeletonList';
import QuickTaskModal from './QuickTaskModal';
import { completeQuickTask, createQuickTask, listQuickTasks } from '../../../api/quickTasks.ts';
import { getDueChip, getQuickTaskAssigneeLabel, getQuickTaskAssignees, formatQuickTaskSourceLabel } from './quickTaskUtils';
import '../../../css/common/quickTask/QuickTask.css';

const EMPTY = [];

// クイックタスク一覧パネル。タスクHome / イベント詳細上部などで再利用する。
export default function QuickTaskPanel({
  filter = 'mine',
  eventUuid = null,
  artistId = null,
  composerSource = null,
  title = 'クイックタスク',
  limit = 8,
  showSource = true,
  inlineAdd = true,
  framed = true,
  showHeader = true,
  showFooterLink = true,
  showShortcutHint = false,
  hideWhenEmpty = false,
  compact = false,
  reloadSignal = 0,
  onOpenComposer,
  onSuccess,
  onError,
}) {
  const [tasks, setTasks] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [completingId, setCompletingId] = useState('');
  const [addTitle, setAddTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [error, setError] = useState('');

  const notifyError = useCallback((msg) => {
    setError(msg || '');
    onError?.(msg);
  }, [onError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { includeDone: false, limit };
      if (eventUuid) query.eventUuid = eventUuid;
      else query.filter = filter;
      const payload = await listQuickTasks(query);
      setTasks(Array.isArray(payload?.items) ? payload.items : EMPTY);
      setError('');
    } catch (e) {
      setTasks(EMPTY);
      notifyError(e?.message || 'クイックタスクの取得に失敗しました。');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [filter, eventUuid, limit, notifyError]);

  useEffect(() => {
    load();
  }, [load, reloadSignal]);

  const handleComplete = async (taskId) => {
    const id = String(taskId || '');
    if (!id || completingId) return;
    setCompletingId(id);
    try {
      await completeQuickTask(id);
      setTasks((prev) => prev.filter((t) => String(t.id || t.taskId) !== id));
      setDetailTask((current) => (String(current?.id || current?.taskId || '') === id ? null : current));
      onSuccess?.('クイックタスクを完了しました。');
    } catch (e) {
      notifyError(e?.message || 'クイックタスクの完了に失敗しました。');
    } finally {
      setCompletingId('');
    }
  };

  const buildSource = useCallback((extra = {}) => ({
    type: 'quick',
    scope: 'quick_task_panel',
    ...(composerSource || {}),
    eventUuid: eventUuid || composerSource?.eventUuid || null,
    artistId: artistId || composerSource?.artistId || null,
    ...extra,
  }), [composerSource, eventUuid, artistId]);

  const handleInlineAdd = async () => {
    const safe = addTitle.trim();
    if (!safe || adding) return;
    setAdding(true);
    try {
      await createQuickTask({
        title: safe,
        priority: 'normal',
        source: buildSource(),
        sourceType: composerSource?.type || 'quick',
        eventUuid: eventUuid || composerSource?.eventUuid || null,
        artistId: artistId || composerSource?.artistId || null,
      });
      setAddTitle('');
      onSuccess?.('クイックタスクを追加しました。');
      await load();
    } catch (e) {
      notifyError(e?.message || 'クイックタスクの追加に失敗しました。');
    } finally {
      setAdding(false);
    }
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInlineAdd();
    }
  };

  const composerSourceWithTitle = buildSource(addTitle.trim() ? { suggestedTitle: addTitle.trim() } : {});
  const openComposer = () => {
    if (onOpenComposer) {
      onOpenComposer(composerSourceWithTitle);
      return;
    }
    setComposerOpen(true);
  };

  const panelClassName = [
    'qt',
    'qt-panel',
    framed ? '' : 'qt-panel--frameless',
    compact ? 'qt-panel--compact' : '',
  ].filter(Boolean).join(' ');

  if (hideWhenEmpty && loaded && !loading && !error && tasks.length === 0) {
    return null;
  }

  return (
    <section className={panelClassName}>
      {showHeader ? (
        <div className="qt-panel__head">
          <h3 className="qt-panel__title">{title}</h3>
          {tasks.length ? <span className="qt-panel__count">{tasks.length}件</span> : null}
          <div className="qt-panel__headActions">
            <DsButton
              type="button"
              variant="secondary"
              size="sm"
              icon
              onClick={load}
              disabled={loading}
              loading={loading}
              aria-label="更新"
            >
              <i className={`fa-solid fa-rotate-right ${loading ? 'fa-spin' : ''}`} aria-hidden="true" />
            </DsButton>
          </div>
        </div>
      ) : null}

      {inlineAdd ? (
        <div className="qt-add">
          <i className="fa-solid fa-plus qt-add__plus" aria-hidden="true" />
          <input
            className="qt-add__input"
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            onKeyDown={handleAddKeyDown}
            placeholder={eventUuid ? 'このイベントにタスクを追加…' : 'タスクを追加…'}
            disabled={adding}
          />
          <DsButton type="button" variant="secondary" size="sm" className="qt-add__detail" onClick={openComposer}>
            詳細
          </DsButton>
          <span className="qt-add__hint">Enter</span>
        </div>
      ) : (
        <div className="qt-add qt-add--standalone">
          <DsButton type="button" variant="secondary" size="sm" className="qt-add__createAction" onClick={openComposer}>
            <i className="fa-solid fa-plus" aria-hidden="true" />
            クイックタスクを作成
          </DsButton>
        </div>
      )}

      {error ? <div className="qt-empty qt-empty--error">{error}</div> : null}

      {loading && tasks.length === 0 ? (
        <QuickTaskSkeletonList count={Math.min(Math.max(Number(limit) || 3, 1), 4)} showSource={showSource} />
      ) : tasks.length > 0 ? (
        <div className="qt-panel__list">
          {tasks.map((task) => {
            const taskId = String(task.id || task.taskId || '');
            return (
              <QuickTaskItem
                key={taskId}
                title={task.title}
                memo={task.description || ''}
                priority={task.priority}
                dueChip={getDueChip(task)}
                assigneeLabel={getQuickTaskAssigneeLabel(task)}
                assignees={getQuickTaskAssignees(task)}
                sourceLabel={showSource ? formatQuickTaskSourceLabel(task) : ''}
                completing={completingId === taskId}
                onComplete={() => handleComplete(taskId)}
                onOpenDetails={() => setDetailTask(task)}
              />
            );
          })}
        </div>
      ) : (
        <p className="qt-empty">未完了のクイックタスクはありません。</p>
      )}

      {showFooterLink ? (
        <div className="qt-panel__footerLink">
          <Link to="/admin/workspaces/task-management/quick-tasks">関連するクイックタスク一覧を見る</Link>
        </div>
      ) : null}
      {showShortcutHint ? (
        <p className="qt-panel__shortcutHint">
          どの管理画面からでも <kbd>Cmd/Ctrl</kbd> + <kbd>Alt/Option</kbd> + <kbd>T</kbd> でクイックタスクを作成できます。
        </p>
      ) : null}

      <QuickTaskModal
        open={composerOpen}
        source={composerSourceWithTitle}
        initialTitle={addTitle.trim()}
        onClose={() => setComposerOpen(false)}
        onCreated={() => {
          setComposerOpen(false);
          setAddTitle('');
          onSuccess?.('クイックタスクを作成しました。');
          load();
        }}
      />

      <QuickTaskDetailModal
        open={Boolean(detailTask)}
        task={detailTask}
        completing={completingId === String(detailTask?.id || detailTask?.taskId || '')}
        onClose={() => setDetailTask(null)}
        onComplete={handleComplete}
        onReminderSent={(nextTask) => {
          if (nextTask?.id || nextTask?.taskId) {
            setDetailTask(nextTask);
          }
          load();
          onSuccess?.('リマインドを送信しました。');
        }}
      />
    </section>
  );
}
